import { z } from "zod";
import { checkDayRange, DayRangeFields, IsoDateTime, LongText, NonEmptyString, PageQuery, QueryBool, ShortText, Uuid } from "./common.js";
import { AnalyteKind } from "./catalogue.js";
import { RICH_TEXT_MAX } from "./rich-text.js";
import { AntibioticPanels, CultureResult, Gram } from "./culture.js";
import { OrderItemState } from "./commerce.js";
import { AttachmentIdsInput, AttachmentInfo, PerformedByLabInput } from "./attachments.js";

// Samples and labels, result capture, validation, report artifacts.
// Flags are mechanical (outside the interval / not the expected text). Colour codes talk to the
// chemist, never to the patient. There is no interpretation field anywhere.

// ---- Samples and labels ---------------------------------------------------------------------

export const SampleState = z.enum(["pending", "collected", "received", "processed", "rejected"]);
export type SampleState = z.infer<typeof SampleState>;

export const Sample = z.object({
  id: Uuid,
  order_id: Uuid,
  /** Per-order folio: order folio + sequence, e.g. "VC-000123-1". Printed as the barcode. */
  label_code: z.string(),
  seq: z.number().int().min(1),
  sample_type_id: Uuid.nullable(),
  sample_type_name: z.string().nullable(),
  container_id: Uuid.nullable(),
  container_name: z.string().nullable(),
  container_colour_hex: z.string().nullable(),
  /** Claves of the studies drawn into this tube. */
  study_claves: z.array(z.string()),
  state: SampleState,
});
export type Sample = z.infer<typeof Sample>;

export const SampleEventKind = z.enum(["collected", "label_printed", "transferred", "received", "rejected", "processed"]);
export type SampleEventKind = z.infer<typeof SampleEventKind>;

export const SampleEventPayload = z.object({
  sample_id: Uuid,
  kind: SampleEventKind,
  reason: ShortText.nullable().default(null),
  /** Destination branch for `transferred`. */
  to_branch_id: Uuid.nullable().default(null),
});
export type SampleEventPayload = z.infer<typeof SampleEventPayload>;

export const LabelsPrintedPayload = z.object({
  order_id: Uuid,
  sample_ids: z.array(Uuid).min(1),
});

/** What the label renderer needs for one tube. */
export const SampleLabel = z.object({
  label_code: z.string(),
  order_folio: z.string(),
  patient_name: z.string(),
  patient_age_text: z.string(),
  sex: z.enum(["M", "F"]),
  container_name: z.string().nullable(),
  container_colour_hex: z.string().nullable(),
  study_claves: z.array(z.string()),
  branch_short_code: z.string(),
  collected_at: IsoDateTime.nullable(),
});
export type SampleLabel = z.infer<typeof SampleLabel>;

export const OrderLabels = z.object({ order_id: Uuid, labels: z.array(SampleLabel) });
export type OrderLabels = z.infer<typeof OrderLabels>;

/** `labels.html?sample_id=…` reprints one tube's label (Muestras pendientes), rejected tubes included. */
export const LabelsQuery = z.object({ sample_id: Uuid.optional() });

/**
 * A tube still to be collected (Muestras pendientes, Very 2026-11-02): `pending` (the patient
 * brings it later) or `rejected` with studies still waiting for a new collection.
 */
export const PendingSample = z.object({
  sample_id: Uuid,
  label_code: z.string(),
  seq: z.number().int(),
  state: z.enum(["pending", "rejected"]),
  order_id: Uuid,
  order_folio: z.string(),
  branch_id: Uuid,
  patient_name: z.string(),
  /** "27 años", "8 meses" at the order's date. */
  patient_age_text: z.string(),
  container_name: z.string().nullable(),
  container_colour_hex: z.string().nullable(),
  sample_type_name: z.string().nullable(),
  /** Claves of the live studies drawn into this tube. */
  study_claves: z.array(z.string()),
  /** Distinct patient indications of those studies, from the frozen snapshot. */
  indications: z.array(z.string()),
  fasting_hours: z.number().int().nullable(),
  condition: z.string().nullable(),
  /** The last rejection's reason, on a rejected tube. */
  rejected_reason: z.string().nullable(),
  registered_at: IsoDateTime,
});
export type PendingSample = z.infer<typeof PendingSample>;

export const PendingSamplesQuery = PageQuery.extend({
  branch_id: Uuid.optional(),
  /** Registration day or range in America/Mexico_City; default = every day. */
  ...DayRangeFields,
  /** A scanned tube code or order folio, matched whole and case-insensitively. */
  code: z.string().trim().min(1).max(60).optional(),
}).superRefine(checkDayRange);
export type PendingSamplesQuery = z.infer<typeof PendingSamplesQuery>;

export const PendingSamplesCountQuery = z.object({ branch_id: Uuid.optional() });
export const PendingSamplesCount = z.object({ count: z.number().int().min(0) });
export type PendingSamplesCount = z.infer<typeof PendingSamplesCount>;

// ---- Results --------------------------------------------------------------------------------

/** Mechanical flag against the applied interval. `A` = text not equal to expected. */
export const ResultFlag = z.enum(["N", "L", "H", "A"]);
export type ResultFlag = z.infer<typeof ResultFlag>;

/** Staff colour code (meeting 2026-10-31): green normal, yellow slightly out, red critical. */
export const ResultColour = z.enum(["green", "yellow", "red", "none"]);
export type ResultColour = z.infer<typeof ResultColour>;

export const ResultSource = z.enum(["typed", "calculated", "imported", "instrument"]);
export type ResultSource = z.infer<typeof ResultSource>;

export const ResultValueInput = z.object({
  analyte_id: Uuid,
  value_numeric: z.number().finite().nullable().default(null),
  /** At most 200 characters for `text`/`reference` (checked against the analyte's kind by the
   *  server); a `document` analyte's formatted text is HTML, sanitised on write (ADR 0019), and
   *  may be longer. The cap here is on the raw input, before sanitising. */
  value_text: z.string().trim().max(RICH_TEXT_MAX * 3).nullable().default(null),
  /** Artifact id for `document` analytes with an attached file. */
  document_artifact_id: Uuid.nullable().default(null),
  /** A culture's outcome, on its `document` analyte only (ADR 0019). */
  culture: CultureResult.nullable().default(null),
  /** Withdraw the value: only for a culture's antibiotic the chemist no longer reports (ADR 0019). */
  clear: z.boolean().default(false),
});
export type ResultValueInput = z.infer<typeof ResultValueInput>;

/** The note under a study (ADR 0019): HTML, sanitised by the server; "" clears it; absent = unchanged. */
export const StudyNoteInput = z.string().max(RICH_TEXT_MAX * 3);

const valuesOrNote = (p: { values: unknown[]; note?: string | undefined; attachments?: unknown[] | undefined; performed_by_lab?: string | undefined }) =>
  p.values.length > 0 || p.note !== undefined || p.attachments !== undefined || p.performed_by_lab !== undefined;

export const ResultsCapturePayload = z
  .object({
    order_item_id: Uuid,
    values: z.array(ResultValueInput).default([]),
    note: StudyNoteInput.optional(),
    /** The study's attached documents (ADR 0030), in print order; absent = unchanged, [] = none. */
    attachments: AttachmentIdsInput.optional(),
    /** «Laboratorio que realizó el estudio» (ADR 0030); absent = unchanged, "" = none. */
    performed_by_lab: PerformedByLabInput.optional(),
    /** When every analyte that needs a value has one, the item moves to `captured`. */
    mark_captured: z.boolean().default(true),
  })
  .refine(valuesOrNote, { message: "Sin valores ni nota que guardar.", path: ["values"] });
export type ResultsCapturePayload = z.infer<typeof ResultsCapturePayload>;

export const ResultValue = z.object({
  id: Uuid,
  order_item_id: Uuid,
  analyte_id: Uuid,
  analyte_code: z.string(),
  analyte_name: z.string(),
  kind: AnalyteKind,
  value_numeric: z.number().nullable(),
  value_text: z.string().nullable(),
  document_artifact_id: Uuid.nullable(),
  culture: CultureResult.nullable(),
  unit: z.string().nullable(),
  flag: ResultFlag.nullable(),
  colour: ResultColour,
  /** The interval row applied, as text, for the chemist's screen. */
  interval_text: z.string().nullable(),
  source: ResultSource,
  entered_by: Uuid,
  entered_at: IsoDateTime,
});
export type ResultValue = z.infer<typeof ResultValue>;

/** Previous value of the same analyte for the same patient, for the capture screen. */
export const PreviousValue = z.object({
  analyte_id: Uuid,
  value_numeric: z.number().nullable(),
  value_text: z.string().nullable(),
  unit: z.string().nullable(),
  validated_at: IsoDateTime,
  delta: z.number().nullable(),
});
export type PreviousValue = z.infer<typeof PreviousValue>;

export const WorklistItem = z.object({
  order_item_id: Uuid,
  order_id: Uuid,
  order_folio: z.string(),
  branch_id: Uuid,
  patient_name: z.string(),
  /** "27 años", "8 meses" at the order's date (Captura's patient list, Fernanda 2026-11-02 20:41). */
  patient_age_text: z.string(),
  patient_sex: z.enum(["M", "F"]),
  study_clave: z.string(),
  study_name: z.string(),
  area_name: z.string().nullable(),
  state: OrderItemState,
  balance_cents: z.number().int(),
  /** The tube for this study is still pending (red flag). */
  sample_pending: z.boolean(),
  sample_label_code: z.string().nullable(),
  registered_at: IsoDateTime,
  /** 'migrated' on the previous system's history (migration 0008). */
  origin: z.enum(["app", "migrated"]),
});
export type WorklistItem = z.infer<typeof WorklistItem>;

export const WorklistQuery = PageQuery.extend({
  branch_id: Uuid.optional(),
  state: OrderItemState.optional(),
  /**
   * Every study of the order whatever its state (validated and issued included; cancelled ones stay out).
   * Captura's patient list uses it to show all of the day's patients; ignored when `state` is given.
   */
  all_states: QueryBool.default(false),
  area_id: Uuid.optional(),
  /** Registration day or range in America/Mexico_City; default = everything open. */
  ...DayRangeFields,
  /** Only orders of this origin; default = both. Validación asks for 'app' unless the migrated history is wanted. */
  origin: z.enum(["app", "migrated"]).optional(),
}).superRefine(checkDayRange);

export const OrderItemResults = z.object({
  order_item_id: Uuid,
  order_id: Uuid,
  order_folio: z.string(),
  study_clave: z.string(),
  study_name: z.string(),
  state: OrderItemState,
  /** The analytes to capture, from the snapshot, in report order, with the interval applied. */
  analytes: z.array(
    z.object({
      analyte_id: Uuid,
      code: z.string(),
      name: z.string(),
      kind: AnalyteKind,
      unit: z.string().nullable(),
      decimals: z.number().int().nullable(),
      options: z.array(z.string()).nullable(),
      section: z.string().nullable(),
      interval_text: z.string().nullable(),
      low: z.number().nullable(),
      high: z.number().nullable(),
      expected_text: z.string().nullable(),
      /** ADR 0023: a numeric analyte with a formula is computed, never typed (`isCalculated`). */
      formula: z.string().nullable().optional(),
    }),
  ),
  /** What a formula may read about the patient, from the snapshot (ADR 0023). */
  formula_patient: z.object({ edad_anios: z.number().int().nullable(), sexo: z.enum(["M", "F"]).nullable() }).optional(),
  values: z.array(ResultValue),
  /** The note under this study on the report (sanitised HTML, ADR 0019); "" = none. */
  note: z.string().default(""),
  /** Organisms to offer on a culture (the lab's own, most used first, then common ones); empty otherwise. */
  organism_suggestions: z.array(z.string()).default([]),
  /** On a culture: the Gram to preselect per organism, keyed by `nameKey` (the lab's latest, ADR 0026); empty otherwise. */
  organism_grams: z.record(z.string(), Gram).optional(),
  /** On a culture: the laboratory's two antibiotic panels (ADR 0026); empty otherwise. */
  antibiotic_panels: AntibioticPanels.optional(),
  /** Report templates of this study's document analytes, by analyte id (current catalogue); prefilled while nothing is saved. */
  document_templates: z.record(z.string(), z.string()).optional(),
  /** The study's attached documents (ADR 0030), in print order; their pages follow the study's page on the report. */
  attachments: z.array(AttachmentInfo).optional(),
  /** «Laboratorio que realizó el estudio» (ADR 0030); "" = the laboratory itself. */
  performed_by_lab: z.string().optional(),
  /** sha256 hex of the current values; send it back in `results.validate` (§6.5). */
  value_hash: z.string().nullable(),
  previous: z.array(PreviousValue),
  validation: z
    .object({
      /** Null on a migrated validation whose validator has no app_user; `user_name` then carries the recorded name. */
      user_id: Uuid.nullable(),
      user_name: z.string(),
      cedula: z.string().nullable(),
      occurred_at: IsoDateTime,
      received_at: IsoDateTime,
    })
    .nullable(),
});
export type OrderItemResults = z.infer<typeof OrderItemResults>;

// ---- Validation -----------------------------------------------------------------------------

export const ValidatePayload = z.object({
  order_item_ids: z.array(Uuid).min(1),
  /** Hash of the values the chemist saw; refused if the stored values differ (§6.5). */
  value_hashes: z.record(z.string(), z.string()).optional(),
});
export type ValidatePayload = z.infer<typeof ValidatePayload>;

export const ReturnForRecapturePayload = z.object({ order_item_id: Uuid, reason: NonEmptyString });

export const AmendPayload = z
  .object({
    order_item_id: Uuid,
    reason: NonEmptyString,
    values: z.array(ResultValueInput).default([]),
    note: StudyNoteInput.optional(),
    attachments: AttachmentIdsInput.optional(),
    performed_by_lab: PerformedByLabInput.optional(),
  })
  .refine(valuesOrNote, { message: "Sin valores ni nota que corregir.", path: ["values"] });
export type AmendPayload = z.infer<typeof AmendPayload>;

export const Validation = z.object({
  id: Uuid,
  order_item_id: Uuid,
  user_id: Uuid,
  cedula: z.string().nullable(),
  occurred_at: IsoDateTime,
  received_at: IsoDateTime,
});
export type Validation = z.infer<typeof Validation>;

// ---- Reports ----------------------------------------------------------------------------------

export const ReportKind = z.enum(["partial", "final", "amendment"]);
export type ReportKind = z.infer<typeof ReportKind>;

export const ReportRenderOptions = z.object({
  /** false = print on the lab's pre-printed letterhead: no header block, no footer identity. */
  letterhead: z.boolean().default(true),
  /** false = the chemist signs by hand: name and cédula print, the signature image does not. */
  signature: z.boolean().default(true),
});
export type ReportRenderOptions = z.infer<typeof ReportRenderOptions>;

export const RenderReportPayload = z.object({
  order_id: Uuid,
  kind: ReportKind,
  /** Validated items to include; empty = all validated items. */
  order_item_ids: z.array(Uuid).default([]),
  options: ReportRenderOptions.default({ letterhead: true, signature: true }),
});

/** Draft preview (never stored): watermarked "Resultados no validados" on unvalidated items. */
export const ReportPreviewQuery = z.object({
  letterhead: QueryBool.default(true),
  signature: QueryBool.default(true),
  /** Same hold as the stored PDF: a preview of an order with a balance is 402 unless a Químico or above asks for a staff view. */
  staff_view: QueryBool.default(false),
});
export type ReportPreviewQuery = z.infer<typeof ReportPreviewQuery>;

/**
 * Captura's «Vista previa»: the report as it would print from the SAVED values, stamped
 * "VISTA PREVIA — SIN VALIDAR", with no signature, validator line or QR. Staff only; never
 * stored, never deliverable, so there is no balance hold. `item_id` limits it to one study.
 */
export const DraftPreviewQuery = z.object({
  letterhead: QueryBool.default(true),
  item_id: Uuid.optional(),
});
export type DraftPreviewQuery = z.infer<typeof DraftPreviewQuery>;

export const ReportPdfQuery = z.object({
  /** Staff (Químico or above) may view a held report; the access event records the override. */
  staff_view: QueryBool.default(false),
  /** `Content-Disposition: attachment` instead of `inline`; same file name either way. */
  download: QueryBool.default(false),
});
export type RenderReportPayload = z.infer<typeof RenderReportPayload>;

/**
 * Which signature block(s) a rendered PDF carries, frozen at render (migration 0013, ADR 0010).
 * `responsible`: one block, the responsable sanitario's. `validator`: one block per validator
 * (`validators`). Null on artifacts rendered before 0013.
 */
export const ReportSignerBlock = z.object({
  user_id: Uuid.nullable(),
  name: z.string(),
  cedula: z.string().nullable(),
  /** Digest of the signature image printed, or null when none was (italic name or hand-signed). */
  signature_sha256_hex: z.string().length(64).nullable(),
});
export type ReportSignerBlock = z.infer<typeof ReportSignerBlock>;

export const ReportArtifactSigner = z.object({
  mode: z.enum(["validator", "responsible"]),
  blocks: z.array(ReportSignerBlock),
});
export type ReportArtifactSigner = z.infer<typeof ReportArtifactSigner>;

export const ReportArtifact = z.object({
  id: Uuid,
  order_id: Uuid,
  kind: ReportKind,
  order_item_ids: z.array(Uuid),
  template_version: z.string(),
  renderer_version: z.string(),
  sha256_hex: z.string().length(64),
  byte_size: z.number().int(),
  rendered_at: IsoDateTime,
  rendered_by: Uuid.nullable(),
  options: ReportRenderOptions,
  /** Who signed this PDF, as printed (null before migration 0013). */
  signer: ReportArtifactSigner.nullable(),
  /** True while the order has an unpaid balance: the PDF is not delivered (staff may view). */
  held_for_balance: z.boolean(),
  /** Suggested filename, no test names (system design §8): Resultados-<folio>.pdf */
  filename: z.string(),
});
export type ReportArtifact = z.infer<typeof ReportArtifact>;

export const ReportsListQuery = z.object({ kind: ReportKind.optional() });

export const DeliveryAttempt = z.object({
  id: Uuid,
  report_artifact_id: Uuid,
  channel: z.enum(["whatsapp", "email", "print", "portal", "in_person"]),
  destination: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.string(),
  user_id: Uuid,
  occurred_at: IsoDateTime,
});
export type DeliveryAttempt = z.infer<typeof DeliveryAttempt>;

export const DeliveryRecordPayload = z.object({
  report_artifact_id: Uuid,
  channel: z.enum(["whatsapp", "email", "print", "portal", "in_person"]),
  /** Confirmed destination (phone/email) or the name of who picked it up. */
  destination: ShortText.nullable().default(null),
  notes: LongText.nullable().default(null),
});
export type DeliveryRecordPayload = z.infer<typeof DeliveryRecordPayload>;
